"use client"
import React, { useRef, useState, useEffect } from "react"
import { Modal, Tag } from "antd"

const FurnitureFinderModal = ({ open, onClose, data }) => {
  const lang = localStorage.getItem("language") || "ar"

  const detail = data?.detail || {}
  const isFailed = detail.status === "failed"
  const imageUrl = detail.input
  const reason = detail.reason
  const date = `${detail.date || ""} ${detail.time || ""}`
  const detectedObjects = detail.image_json?.detected_objects || []
  const [frameW, frameH] = detail.image_json?.frame_size || [1920, 1080]

  const imageRef = useRef(null)
  const containerRef = useRef(null)
  const [transform, setTransform] = useState({
    scale: 1,
    offsetX: 0,
    offsetY: 0,
  })
  const [imageLoaded, setImageLoaded] = useState(false)

  const computeTransform = () => {
    if (!imageRef.current || !containerRef.current || !imageLoaded) return
    const img = imageRef.current
    const cont = containerRef.current

    const cW = cont.clientWidth
    const cH = cont.clientHeight

    // Ensure we have valid dimensions
    if (cW === 0 || cH === 0) return

    const imgNaturalW = img.naturalWidth
    const imgNaturalH = img.naturalHeight

    if (
      !imgNaturalW ||
      !imgNaturalH ||
      imgNaturalW === 0 ||
      imgNaturalH === 0
    ) {
      return
    }

    let [frameW, frameH] = detail.image_json?.frame_size || [
      imgNaturalW,
      imgNaturalH,
    ]

    if (!frameW || !frameH || frameW === 0 || frameH === 0) {
      frameW = imgNaturalW
      frameH = imgNaturalH
    }

    const frameAspect = frameW / frameH
    const imgAspect = imgNaturalW / imgNaturalH
    const aspectDiff = Math.abs(frameAspect - imgAspect)

    // If aspect ratio difference is > 10%, use image dimensions instead
    if (aspectDiff > 0.1) {
      frameW = imgNaturalW
      frameH = imgNaturalH
    }

    // imgAspect is already calculated above
    const contAspect = cW / cH

    let displayW, displayH
    if (imgAspect > contAspect) {
      // Image is wider - fit to width
      displayW = cW
      displayH = cW / imgAspect
    } else {
      // Image is taller - fit to height
      displayH = cH
      displayW = cH * imgAspect
    }

    const scaleX = displayW / frameW
    const scaleY = displayH / frameH
    // Use the average or X scale (they should be very close if frame_size matches image)
    const scale = scaleX

    // Calculate offsets to center the image
    const offsetX = (cW - displayW) / 2
    const offsetY = (cH - displayH) / 2

    setTransform({ scale, offsetX, offsetY })
  }

  const handleImageLoad = () => {
    setImageLoaded(true)
    // Use multiple attempts to ensure calculation happens after render
    requestAnimationFrame(() => {
      setTimeout(() => {
        computeTransform()
        // Second attempt after a short delay to ensure everything is rendered
        setTimeout(() => {
          computeTransform()
        }, 50)
      }, 50)
    })
  }

  useEffect(() => {
    if (open) {
      if (imageLoaded) {
        // Recalculate when modal opens and image is loaded
        requestAnimationFrame(() => {
          setTimeout(() => {
            computeTransform()
            // Second attempt to ensure accuracy
            setTimeout(() => {
              computeTransform()
            }, 50)
          }, 50)
        })
      }
      window.addEventListener("resize", computeTransform)
    }
    return () => {
      window.removeEventListener("resize", computeTransform)
    }
  }, [open, imageUrl, imageLoaded])

  // Reset image loaded state when image URL changes
  useEffect(() => {
    if (imageUrl) {
      setImageLoaded(false)
    }
  }, [imageUrl])

  const getPolygonPoints = (polygon) => {
    if (!polygon || !Array.isArray(polygon) || polygon.length === 0) return ""
    const { scale, offsetX, offsetY } = transform

    // Validate and transform polygon points
    const points = polygon
      .filter((point) => Array.isArray(point) && point.length >= 2)
      .map(([x, y]) => {
        const scaledX = (x || 0) * scale + offsetX
        const scaledY = (y || 0) * scale + offsetY
        return `${scaledX},${scaledY}`
      })

    return points.join(" ")
  }

  return (
    <Modal
      title={
        <div className="d-flex">
          <div
            className={
              lang === "ar" ? "r-r-modal-icon-arabic" : "r-r-modal-icon"
            }
          >
            <img src="/dashboard/room-redesign-v2.svg" alt="" />
          </div>
          <div>
            <div className="modal-heading">
              {lang === "ar" ? "مكتشف الأثاث" : "Furniture Finder"}
            </div>
            <p className="modal-timestamp">{date || "—"}</p>
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={820}
      bodyStyle={{
        display: "flex",
        flexDirection: "column",
        height: "600px",
      }}
    >
      <p style={{ fontStyle: "italic", marginBottom: 12, color: "#555" }}>
        {isFailed
          ? lang === "ar"
            ? "فشل في معالجة الصورة."
            : "Image processing failed."
          : lang === "ar"
          ? `تمت معالجة الصورة واكتشاف ${detectedObjects.length} عنصر بنجاح`
          : `Processed image and detected ${detectedObjects.length} objects successfully`}
      </p>

      {/* 🔹 Image container */}
      <div
        ref={containerRef}
        style={{
          position: "relative",
          width: "100%",
          height: "500px",
          backgroundColor: "#f4f4f4",
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <img
          ref={imageRef}
          src={imageUrl}
          alt="Detected"
          onLoad={handleImageLoad}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />

        {/* 🔹 Overlay polygons */}
        <svg
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        >
          <defs>
            <linearGradient
              id="snakeGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="50%" stopColor="#80CBDD" />
              <stop offset="100%" stopColor="#308AA3" />
            </linearGradient>
          </defs>

          {detectedObjects.map((obj, i) => (
            <polygon
              key={i}
              points={getPolygonPoints(obj.mask_polygon)}
              style={{
                fill: "rgba(128, 202, 221, 0)",
                stroke: "url(#snakeGradient)",
                strokeWidth: 2,
              }}
            />
          ))}
        </svg>
      </div>

      {/* 🔹 Info Section */}
      <div
        style={{
          marginTop: 16,
          border: "1px solid #eee",
          borderRadius: "10px",
          padding: "12px",
          background: isFailed ? "#fff5f5" : "rgba(250,250,250,0.9)",
        }}
      >
        {isFailed ? (
          <div
            style={{
              color: "#d93025",
              border: "1px solid #f5c2c2",
              borderRadius: "8px",
              padding: "10px",
              fontWeight: 500,
            }}
          >
            {lang === "ar" ? "السبب:" : "Reason:"}
            <br />
            <span style={{ color: "red" }}>
              {reason ||
                (lang === "ar" ? "لم يتم توفير سبب" : "No reason provided")}
            </span>
          </div>
        ) : (
          <>
            <div
              style={{
                fontSize: "12px",
                fontWeight: "400",
                color: "#404040",
                paddingBottom: "10px",
              }}
            >
              {lang === "ar" ? "العناصر المكتشفة" : "Objects Detected"}
            </div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: "400",
                color: "#404040",
              }}
            >
              {detectedObjects.length} {lang === "ar" ? "عنصر" : "objects"}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

export default FurnitureFinderModal
